import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { API_BASE_URL } from "../config/api";
import {
  authEvents,
  clearAuthStorage,
  getAuthToken,
  handleSessionExpired,
  isTokenExpired,
} from "../utils/auth";
import { ACTION_TO_FLAG, RbacAction, RbacModule } from "../rbac/modules";
import { PermissionMap, RbacContextValue } from "../rbac/types";

interface User {
  id: string;
  email: string;
  role: string;
  phone: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  rbac: RbacContextValue | null;
  can: (module: RbacModule, action: RbacAction) => boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => boolean;
  refreshPermissions: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const fetchMe = async (token: string, signal?: AbortSignal) => {
  const res = await fetch(`${API_BASE_URL}/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!res.ok) throw new Error(`auth/me ${res.status}`);
  const json = await res.json();
  return json.data as { user: User; rbac: RbacContextValue | null };
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [rbac, setRbac] = useState<RbacContextValue | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshPermissions = useCallback(async (signal?: AbortSignal) => {
    const token = getAuthToken();
    if (!token || isTokenExpired(token)) return;
    try {
      const { user: fresh, rbac: freshRbac } = await fetchMe(token, signal);
      setUser(fresh);
      setRbac(freshRbac);
      localStorage.setItem("user", JSON.stringify(fresh));
    } catch (e: any) {
      if (e.name === "AbortError") return;
      // Session expiry is handled by the fetch interceptor.
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const bootstrap = async () => {
      const token = getAuthToken();
      const userStr = localStorage.getItem("user");

      if (token && isTokenExpired(token)) {
        handleSessionExpired();
        setIsLoading(false);
        return;
      }

      if (token && userStr) {
        try {
          setUser(JSON.parse(userStr) as User);
        } catch {
          clearAuthStorage();
        }
        await refreshPermissions(controller.signal);
      }
      setIsLoading(false);
    };

    bootstrap();

    const handleAuthSessionExpired = () => {
      setUser(null);
      setRbac(null);
      setIsLoading(false);
    };
    window.addEventListener(
      authEvents.sessionExpired,
      handleAuthSessionExpired,
    );
    return () => {
      controller.abort();
      window.removeEventListener(
        authEvents.sessionExpired,
        handleAuthSessionExpired,
      );
    };
  }, [refreshPermissions]);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/auth/admin-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!response.ok) throw new Error("Invalid credentials");

      const data = await response.json();
      if (!(data.success && data.data)) throw new Error("Login failed");

      const { accessToken, user: userData } = data.data;
      if (userData.role !== "ADMIN") {
        throw new Error("Access denied. Admin role required.");
      }

      localStorage.setItem("authToken", accessToken);
      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);

      // Load permissions — token invalid on next call if user lost access.
      try {
        const { rbac: freshRbac } = await fetchMe(accessToken);
        setRbac(freshRbac);
      } catch {
        setRbac(null);
      }
    } catch (error: any) {
      throw new Error(error.message || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    clearAuthStorage();
    setUser(null);
    setRbac(null);
  };

  const checkAuth = (): boolean => {
    const token = getAuthToken();
    return !!token && !isTokenExpired(token);
  };

  const can = useCallback(
    (module: RbacModule, action: RbacAction): boolean => {
      if (!rbac) return false;
      if (rbac.isSuperAdmin) return true;
      const modPerms = rbac.permissions[module];
      if (!modPerms) return false;
      const flagKey = ACTION_TO_FLAG[action];
      // Support both action-keyed and flag-keyed shapes (backend returns action-keyed).
      return Boolean(
        (modPerms as any)[action] ??
        (modPerms as unknown as Record<string, boolean>)[flagKey],
      );
    },
    [rbac],
  );

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    rbac,
    can,
    login,
    logout,
    checkAuth,
    refreshPermissions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined)
    throw new Error("useAuth must be used within an AuthProvider");
  return context;
};

// Convenience export for permission-typed consumers.
export type { PermissionMap };
